import { useEffect, useState } from 'react';
import { getCurrentYearTeachersData, getLegacyEducatorLogins, getNewEducatorLogins, lookupNewDBStartDate } from '../reportUtilities';
import Spinner from 'react-spinkit';
import Table from '../Tables/Table';
import Tooltip from '../../components/Tooltip';
import generalStyles from './SchoolReport.module.scss';
import styles from './TopEngagedTeachers.module.scss';
import { TeacherClassroomMaps, ReportingMixpanelEvent } from '@peekapak/types/ReportDataExchangeTypes';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';

interface Props {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  selectedSchoolYear: string;
  userList: UserProfileType[];
  userListReady: boolean;
}

const TopEngagedTeachers = ({
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
  userListReady,
  selectedSchoolYear,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
}: Props): JSX.Element => {
  const [myState, setMyState] = useState({
    dataMap: {},
    dataMapReady: false,
    otherLogins: 0,
  });

  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: {},
      dataMapReady: false,
      otherLogins: 0,
    }));
  }, [legacyPlatformEvents, legacyPeekavilleEvents, newMixpanelEvents]);

  useEffect(() => {
    if (
      legacyPlatformEventsReady &&
      legacyPeekavilleEventsReady &&
      teacherClassroomMapsReady &&
      newMixpanelEventsReady &&
      !myState.dataMapReady
    ) {
      buildData();
    }

    function buildData() {
      const newTeacherMap = getCurrentYearTeachersData(
        userList,
        teacherClassroomMaps,
        legacyPlatformEvents,
        newMixpanelEvents,
      );

      const allStaffEvents = [
        ...legacyPlatformEvents.filter((e) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(e.type)),
        ...newMixpanelEvents.filter((e) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(e.type)),
      ];
      const totalEducatorLogins =
        getLegacyEducatorLogins(allStaffEvents) + getNewEducatorLogins(allStaffEvents);
      const rosteredLoginSum = Object.values(newTeacherMap).reduce(
        (sum: number, t: any) => sum + (t.loginTimes || 0),
        0,
      );
      const otherLogins = totalEducatorLogins - rosteredLoginSum;

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newTeacherMap,
        dataMapReady: true,
        otherLogins,
      }));
    }
  }, [
    teacherClassroomMaps,
    teacherClassroomMapsReady,
    legacyPeekavilleEventsReady,
    legacyPeekavilleEvents,
    legacyPlatformEvents,
    legacyPlatformEventsReady,
    newMixpanelEvents,
    newMixpanelEventsReady,
    selectedSchoolYear,
    myState.dataMap,
    myState.dataMapReady,
    userList,
  ]);

  return (
    <div className={styles.topEngagedTeachers}>
      <h4 className={generalStyles.tableHeader}>Top Engaged Teachers</h4>
      <Table
        // tableHeader={['Full Name', 'Logins', 'Total Time (min)']}
        tableHeader={[{ title: 'Full Name' }, { title: 'Logins' }]}
      >
        {getContent()}
      </Table>
    </div>
  );

  function getContent() {
    if (!legacyPlatformEventsReady || !userListReady || !legacyPeekavilleEventsReady || !newMixpanelEventsReady) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>
              Loading
              <Spinner name='line-scale' />
            </div>
          </td>
        </tr>
      );
    }

    return <>{getTopEngagedTeachers()}</>;
  }

  function getTopEngagedTeachers() {
    const sorted = Object.values(myState.dataMap).sort((a: any, b: any) => b.loginTimes - a.loginTimes);

    const entries: any[] = sorted.slice(0, Math.min(5, sorted.length));

    if (myState.otherLogins > 0 && (entries.length < 5 || myState.otherLogins > (entries[entries.length - 1]?.loginTimes || 0))) {
      entries.push({ other: true, loginTimes: myState.otherLogins });
      entries.sort((a: any, b: any) => b.loginTimes - a.loginTimes);
      entries.splice(5);
    }

    if (entries.length === 0) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>No data available</div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {entries.map((e: any) =>
          e.other ? (
            <tr key='other'>
              <td>Other (Unassociated)</td>
              <td>{e.loginTimes}</td>
            </tr>
          ) : (
            <tr key={e.username}>
              <td>
                {e.email || e.userId ? (
                  <Tooltip content={e.email || e.userId} direction='bottom' color='blue'>
                    <span>{`${e.firstName} ${e.lastName}`}</span>
                  </Tooltip>
                ) : (
                  `${e.firstName} ${e.lastName}`
                )}
              </td>
              <td>{e.loginTimes}</td>
            </tr>
          ),
        )}
      </>
    );
  }
};

export default TopEngagedTeachers;
